/**
 * Listing Filters Module
 * Handles AJAX-based filtering and sorting for listing blocks with URL query support
 */

const ListingFilters = {
    init() {
        this.elements = {
            containers: document.querySelectorAll('[data-listing-filters]'),
        };

        if (this.elements.containers.length === 0) return;

        this.elements.containers.forEach((container) => {
            this.setupFilters(container);
        });

        // Handle browser back/forward buttons
        window.addEventListener('popstate', () => {
            location.reload();
        });
    },

    updateCustomDropdown(selectElement, value) {
        // Find the custom dropdown parent
        const customDropdown = selectElement.nextElementSibling;

        if (customDropdown && customDropdown.classList.contains('custom-dropdown')) {
            // Get the label element (right before the ul.dropdown)
            const customDropdownText = customDropdown.querySelector('.custom-dropdown-text');

            if (customDropdownText) {
                // Find which option has this value
                const selectedOption = selectElement.querySelector(`option[value="${value}"]`);

                if (selectedOption) {
                    // Update the text to show selected value
                    customDropdownText.textContent = selectedOption.textContent;

                    // Add or remove the "selected" class
                    if (value && value !== '') {
                        customDropdown.classList.add('selected');
                    } else {
                        customDropdown.classList.remove('selected');
                    }
                }
            }
        }

        // Set the actual select value
        selectElement.value = value;
    },

    setupFilters(filterContainer) {
        const grid = filterContainer.parentElement.querySelector('[data-listing-grid]');
        if (!grid) return;

        const categorySelect = filterContainer.querySelector('[data-listing-category]');
        const sortSelect = filterContainer.querySelector('[data-listing-sort]');
        const locationSelect = filterContainer.querySelector('[data-listing-location]');
        const typeSelect = filterContainer.querySelector('[data-listing-type]');
        const brandSelect = filterContainer.querySelector('[data-listing-brand]');
        const levelSelect = filterContainer.querySelector('[data-listing-level]');
        const countElement = filterContainer.querySelector('[data-listing-count]');
        const loader = grid.querySelector('[data-listing-loader]');
        const paginationContainer = filterContainer.parentElement.querySelector('[data-listing-pagination]');

        // Get block content from data attribute
        const blockContent = grid.dataset.blockContent;
        const defaultSort = grid.dataset.defaultSort || 'date_desc';

        // Store current page
        let currentPage = 1;

        // Read URL parameters and apply to filters
        const urlParams = this.getUrlParams();
        console.log('URL Params:', urlParams);
        console.log('Category Select:', categorySelect);

        if (urlParams.category && categorySelect) {
            console.log('Setting category to:', urlParams.category);
            this.updateCustomDropdown(categorySelect, urlParams.category);
            console.log('Category value after set:', categorySelect.value);
        }
        if (urlParams.location && locationSelect) {
            this.updateCustomDropdown(locationSelect, urlParams.location);
        }
        if (urlParams.type && typeSelect) {
            this.updateCustomDropdown(typeSelect, urlParams.type);
        }
        if (urlParams.brand && brandSelect) {
            this.updateCustomDropdown(brandSelect, urlParams.brand);
        }
        if (urlParams.level && levelSelect) {
            this.updateCustomDropdown(levelSelect, urlParams.level);
        }
        if (urlParams.sort && sortSelect) {
            this.updateCustomDropdown(sortSelect, urlParams.sort);
        }
        if (urlParams.page) {
            currentPage = parseInt(urlParams.page);
        }

        // Set up event listeners
        if (categorySelect) {
            categorySelect.addEventListener('change', () => {
                currentPage = 1; // Reset to page 1 on filter change
                this.handleFilterChange(grid, categorySelect, sortSelect, locationSelect, typeSelect, brandSelect, levelSelect, loader, countElement, paginationContainer, blockContent, currentPage);
            });
        }

        if (sortSelect) {
            sortSelect.addEventListener('change', () => {
                currentPage = 1;
                this.handleFilterChange(grid, categorySelect, sortSelect, locationSelect, typeSelect, brandSelect, levelSelect, loader, countElement, paginationContainer, blockContent, currentPage);
            });
        }

        if (locationSelect) {
            locationSelect.addEventListener('change', () => {
                currentPage = 1;
                this.handleFilterChange(grid, categorySelect, sortSelect, locationSelect, typeSelect, brandSelect, levelSelect, loader, countElement, paginationContainer, blockContent, currentPage);
            });
        }

        if (typeSelect) {
            typeSelect.addEventListener('change', () => {
                currentPage = 1;
                this.handleFilterChange(grid, categorySelect, sortSelect, locationSelect, typeSelect, brandSelect, levelSelect, loader, countElement, paginationContainer, blockContent, currentPage);
            });
        }

        if (brandSelect) {
            brandSelect.addEventListener('change', () => {
                currentPage = 1;
                this.handleFilterChange(grid, categorySelect, sortSelect, locationSelect, typeSelect, brandSelect, levelSelect, loader, countElement, paginationContainer, blockContent, currentPage);
            });
        }

        if (levelSelect) {
            levelSelect.addEventListener('change', () => {
                currentPage = 1;
                this.handleFilterChange(grid, categorySelect, sortSelect, locationSelect, typeSelect, brandSelect, levelSelect, loader, countElement, paginationContainer, blockContent, currentPage);
            });
        }

        // Pagination click handler (delegated)
        if (paginationContainer) {
            paginationContainer.addEventListener('click', (e) => {
                const pageButton = e.target.closest('[data-page]');
                if (pageButton) {
                    e.preventDefault();
                    currentPage = parseInt(pageButton.dataset.page);
                    this.handleFilterChange(grid, categorySelect, sortSelect, locationSelect, typeSelect, brandSelect, levelSelect, loader, countElement, paginationContainer, blockContent, currentPage);
                }
            });
        }

        // If URL params exist and page > 1, trigger the filter to load that page
        const hasFilterParams = urlParams.category || urlParams.location || urlParams.type || urlParams.brand || urlParams.level || urlParams.sort;

        if (urlParams.page && parseInt(urlParams.page) > 1) {
            currentPage = parseInt(urlParams.page);
            this.handleFilterChange(grid, categorySelect, sortSelect, locationSelect, typeSelect, brandSelect, levelSelect, loader, countElement, paginationContainer, blockContent, currentPage);
        } else if (hasFilterParams) {
            // Trigger filter if any query params exist
            currentPage = 1;
            this.handleFilterChange(grid, categorySelect, sortSelect, locationSelect, typeSelect, brandSelect, levelSelect, loader, countElement, paginationContainer, blockContent, currentPage);
        }

        // Initialize count
        this.updateCount(countElement, grid);
    },

    handleFilterChange(grid, categorySelect, sortSelect, locationSelect, typeSelect, brandSelect, levelSelect, loader, countElement, paginationContainer, blockContent, page = 1) {
        // Show loader
        if (loader) loader.style.display = 'block';
        if (grid) grid.style.opacity = '0.5';

        // Get current filter values
        const category = categorySelect ? categorySelect.value : '';
        const sort = sortSelect ? sortSelect.value : defaultSort;
        const location = locationSelect ? locationSelect.value : '';
        const type = typeSelect ? typeSelect.value : '';
        const brand = brandSelect ? brandSelect.value : '';
        const level = levelSelect ? levelSelect.value : '';

        // Prepare AJAX data
        const data = new FormData();
        data.append('action', 'listing_filter');
        data.append('nonce', listingFilterData.nonce);
        data.append('category', category);
        data.append('sort', sort);
        data.append('location', location);
        data.append('type', type);
        data.append('brand', brand);
        data.append('level', level);
        data.append('page', page);
        data.append('content', blockContent);

        // Make AJAX request
        fetch(listingFilterData.ajax_url, {
            method: 'POST',
            body: data,
        })
            .then((response) => response.json())
            .then((result) => {
                console.log('AJAX result:', result); // Debug
                if (result.success) {
                    console.log('HTML received length:', result.data.html.length); // Debug
                    console.log('HTML preview:', result.data.html.substring(0, 300)); // Debug first 300 chars

                    // Store the loader element before updating innerHTML
                    const loaderElement = grid.querySelector('[data-listing-loader]');

                    console.log('Grid children before clear:', grid.children.length); // Debug

                    // Clear grid except loader - remove all articles/content
                    Array.from(grid.children).forEach(child => {
                        if (child !== loaderElement) {
                            console.log('Removing child:', child.className); // Debug
                            child.remove();
                        }
                    });

                    console.log('Grid children after clear:', grid.children.length); // Debug

                    // Create new content from HTML string
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = result.data.html.trim();

                    console.log('Temp div children:', tempDiv.children.length); // Debug
                    console.log('First child class:', tempDiv.children[0]?.className); // Debug

                    // Insert all new articles before loader
                    const fragment = document.createDocumentFragment();
                    while (tempDiv.firstChild) {
                        fragment.appendChild(tempDiv.firstChild);
                    }
                    grid.insertBefore(fragment, loaderElement);

                    console.log('Grid children after insert:', grid.children.length); // Debug

                    // Update global team data if present in response
                    if (result.data.teamData && Array.isArray(result.data.teamData)) {
                        if (!window.teamMembersData) {
                            window.teamMembersData = [];
                        }
                        // Merge new team data, avoiding duplicates
                        result.data.teamData.forEach(newMember => {
                            const existingIndex = window.teamMembersData.findIndex(m => m.id === newMember.id);
                            if (existingIndex >= 0) {
                                window.teamMembersData[existingIndex] = newMember;
                            } else {
                                window.teamMembersData.push(newMember);
                            }
                        });
                    }

                    // Update count
                    if (countElement) {
                        const itemText = result.data.count === 1 ? 'item' : 'items';
                        countElement.textContent = `${result.data.count} ${itemText} found`;
                    }

                    // Update URL with current filters
                    this.updateUrl(categorySelect, sortSelect, locationSelect, typeSelect, brandSelect, levelSelect, page);

                    // Update pagination
                    if (paginationContainer && result.data.max_pages > 1) {
                        this.renderPagination(paginationContainer, result.data.current_page, result.data.max_pages);
                    } else if (paginationContainer) {
                        paginationContainer.innerHTML = '';
                    }

                    // Hide loader
                    if (loader) loader.style.display = 'none';
                    if (grid) grid.style.opacity = '1';

                    // Scroll to top of grid
                    // grid.scrollIntoView({ behavior: 'smooth', block: 'start' });

                    // Trigger custom event for other scripts
                    const event = new CustomEvent('listingFiltered', {
                        detail: { count: result.data.count, page: result.data.current_page },
                    });
                    grid.dispatchEvent(event);
                } else {
                    console.error('Filter request failed:', result);
                    this.showError(grid, loader);
                }
            })
            .catch((error) => {
                console.error('AJAX error:', error);
                this.showError(grid, loader);
            });
    },

    showError(grid, loader) {
        if (grid) {
            const loaderElement = grid.querySelector('[data-listing-loader]');
            Array.from(grid.children).forEach(child => {
                if (child !== loaderElement) {
                    child.remove();
                }
            });
            const errorMsg = document.createElement('p');
            errorMsg.className = 'error-message';
            errorMsg.textContent = 'An error occurred. Please try again.';
            grid.insertBefore(errorMsg, loaderElement);
            grid.style.opacity = '1';
        }
        if (loader) loader.style.display = 'none';
    },

    updateCount(countElement, grid) {
        if (!countElement || !grid) return;

        const items = grid.querySelectorAll('[class*="article-"]');
        const count = items.length;
        const itemText = count === 1 ? 'item' : 'items';
        countElement.textContent = `${count} ${itemText} found`;
    },

    renderPagination(container, currentPage, maxPages) {
        if (!container) return;

        let html = '';

        // Previous button
        if (currentPage > 1) {
            html += `<button class="c-pagination__button c-pagination__button--prev" data-page="${currentPage - 1}" aria-label="Previous page">Previous</button>`;
        }

        // Page numbers
        html += '<div class="c-pagination__numbers">';

        // Always show first page
        if (currentPage > 3) {
            html += `<button class="c-pagination__button c-pagination__button--number" data-page="1">1</button>`;
            if (currentPage > 4) {
                html += '<span class="c-pagination__ellipsis">...</span>';
            }
        }

        // Show pages around current page
        for (let i = Math.max(1, currentPage - 2); i <= Math.min(maxPages, currentPage + 2); i++) {
            const activeClass = i === currentPage ? ' c-pagination__button--active' : '';
            html += `<button class="c-pagination__button c-pagination__button--number${activeClass}" data-page="${i}" ${i === currentPage ? 'aria-current="page"' : ''}>${i}</button>`;
        }

        // Always show last page
        if (currentPage < maxPages - 2) {
            if (currentPage < maxPages - 3) {
                html += '<span class="c-pagination__ellipsis">...</span>';
            }
            html += `<button class="c-pagination__button c-pagination__button--number" data-page="${maxPages}">${maxPages}</button>`;
        }

        html += '</div>';

        // Next button
        if (currentPage < maxPages) {
            html += `<button class="c-pagination__button c-pagination__button--next" data-page="${currentPage + 1}" aria-label="Next page">Next</button>`;
        }

        container.innerHTML = html;
    },

    getUrlParams() {
        const params = new URLSearchParams(window.location.search);
        return {
            category: params.get('category') || '',
            location: params.get('location') || '',
            type: params.get('type') || '',
            brand: params.get('brand') || '',
            level: params.get('level') || '',
            sort: params.get('sort') || '',
            page: params.get('page') || '1',
        };
    },

    updateUrl(categorySelect, sortSelect, locationSelect, typeSelect, brandSelect, levelSelect, page) {
        const params = new URLSearchParams();

        if (categorySelect && categorySelect.value) {
            params.set('category', categorySelect.value);
        }
        if (locationSelect && locationSelect.value) {
            params.set('location', locationSelect.value);
        }
        if (typeSelect && typeSelect.value) {
            params.set('type', typeSelect.value);
        }
        if (brandSelect && brandSelect.value) {
            params.set('brand', brandSelect.value);
        }
        if (levelSelect && levelSelect.value) {
            params.set('level', levelSelect.value);
        }
        if (sortSelect && sortSelect.value && sortSelect.value !== 'date_desc') {
            params.set('sort', sortSelect.value);
        }
        if (page && page > 1) {
            params.set('page', page);
        }

        const queryString = params.toString();
        const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

        window.history.pushState({ path: newUrl }, '', newUrl);
    },
};

export default ListingFilters;
